// Helpers PUROS del modo "Redes" — ADR-2026-10-01-modo-redes.
//
// El modo es Prompt libre (imagen) y el Creador de video (video) en UNA pantalla con un
// interruptor, y el lienzo del Agente para lo generado. Lo que tiene de lógica propia
// —el reparto de adjuntos en Producto / Modelo / el resto, el costo según el medio, qué
// falta para generar— vive acá para probarse sin React. Lo demás lo toma de los dos
// modos de origen: `creditosPromptLibre`, `creditosCreadorVideo`, `faltaCreadorVideo`.

import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2, getFormatLabel } from '../../mockData';
import { esFormatoPersonalizado } from '../../../../utils/formatoPersonalizado';
import type { ResolucionImagen } from '../../../../lib/resolucionImagen';
import { RESOLUCION_DEFAULT } from '../../../../lib/resolucionImagen';
import { GENERAL } from '../../videoTiposCatalog';
import { creditosPromptLibre, faltaPromptLibre } from '../promptLibre/promptLibre';
import { avisoDeVideoDeReferencia, segundosDeEntradaACobrar } from '../../../../utils/videoDeReferencia';
import { creditsForVideoGeneral, safeResolution } from '../../../../utils/credits';
import {
  AJUSTES_VIDEO_DEFAULT,
  FORMATO_DEFAULT_VIDEO,
  creditosCreadorVideo,
  faltaCreadorVideo,
  type AjustesVideo,
} from '../creadorVideo/creadorVideo';

export const RUTA_REDES = '/redes';
export const rutaDeSesion = (id: string): string => `${RUTA_REDES}/${id}`;

/** Lo que se está por generar: una imagen o un video. */
export type Medio = 'imagen' | 'video';

/** Formato con el que abre Imagen: 4:5, el posteo. Video abre en 9:16 como el Creador. */
export const FORMATO_DEFAULT_IMAGEN = 'post';

/**
 * Los ajustes de la caja, juntos. Cada medio tiene los suyos y se CONSERVAN al cambiar de
 * medio: pasar a Video y volver no pierde la cantidad ni la calidad de Imagen.
 */
export interface AjustesDeRedes {
  medio: Medio;
  imagen: {
    formatSlug: string;
    calidad: ResolucionImagen;
    cantidad: number;
  };
  video: {
    formatSlug: string;
    ajustes: AjustesVideo;
    tipoVideo: string;
  };
  /**
   * El MODO elegido en la tarjeta "Modo" de la caja (ADR-2026-10-09-modos-adentro-de-crear,
   * supersede la pastilla de ADR-2026-10-09-automatico-adentro-de-crear). Sin modo, Crear de
   * siempre. Cada modo fija su medio y se limpia al cambiar de medio:
   *  - `automatico` (Imagen): Atlas arma una escena DISTINTA por imagen con los productos (el
   *    modo Automático, `modo: 'rapido'`); el texto es opcional y modelo y referencias no viajan.
   *  - `animar` (Video): anima UNA foto de "Añadir referencia" como primer cuadro (el modo
   *    Animar imagen); productos y modelos no viajan; de 4 a 15 s, 1 crédito por segundo.
   */
  modo?: ModoDeCrear;
}

export type ModoDeCrear = 'automatico' | 'animar';

/** Los modos que ofrece la tarjeta "Modo", en su orden. */
export const MODOS_DE_CREAR: {
  id: ModoDeCrear;
  nombre: string;
  bajada: string;
  medio: Medio;
  /** La carátula del modo (la de la sidebar y el sheet), y su clip si tiene. */
  portada: string;
  video?: string;
}[] = [
  {
    id: 'automatico',
    nombre: 'Imagen automática',
    bajada: 'Atlas arma escenas distintas con tu producto',
    medio: 'imagen',
    portada: '/images/modo_automatico/caratula_cuadrada.jpg',
  },
  {
    id: 'animar',
    nombre: 'Animar imagen',
    bajada: 'Convertí una foto en video',
    medio: 'video',
    portada: '/images/modo_animar_imagen/caratula_poster.jpg',
    video: '/images/modo_animar_imagen/caratula.mp4',
  },
];

/** Duración de Animar imagen: la misma escala que el modo (`AnimarImagenMode`). */
export const ANIMAR_MIN_SEG = 4;
export const ANIMAR_MAX_SEG = 15;
export const duracionDeAnimar = (seg: number): number => Math.min(ANIMAR_MAX_SEG, Math.max(ANIMAR_MIN_SEG, seg));

/** Con cuántos segundos arranca Animar: el default del modo (`AnimarImagenMode`). */
export const ANIMAR_DEFAULT_SEG = 8;

/**
 * Elige un modo (o ninguno): pasa al medio del modo. Entrar a Animar pone la duración en la
 * del modo, 8 s: heredar la del video (hasta 30 s) arrancaba en el tope de 15 créditos.
 */
export function conModo(a: AjustesDeRedes, modo: ModoDeCrear | undefined): AjustesDeRedes {
  const medio = MODOS_DE_CREAR.find((m) => m.id === modo)?.medio ?? a.medio;
  const entraAAnimar = modo === 'animar' && a.modo !== 'animar';
  return {
    ...a,
    modo,
    medio,
    ...(entraAAnimar
      ? { video: { ...a.video, ajustes: { ...a.video.ajustes, durationSeconds: ANIMAR_DEFAULT_SEG } } }
      : {}),
  };
}

/** Cambia de medio; el modo, que es de un medio, se va. */
export const conMedio = (a: AjustesDeRedes, medio: Medio): AjustesDeRedes =>
  a.medio === medio ? a : { ...a, medio, modo: undefined };

export const AJUSTES_DEFAULT: AjustesDeRedes = {
  medio: 'imagen',
  imagen: { formatSlug: FORMATO_DEFAULT_IMAGEN, calidad: RESOLUCION_DEFAULT, cantidad: 1 },
  video: { formatSlug: FORMATO_DEFAULT_VIDEO, ajustes: AJUSTES_VIDEO_DEFAULT, tipoVideo: GENERAL.slug },
};

/** El formato del medio activo. */
export function formatoActivo(a: AjustesDeRedes): string {
  return a.medio === 'video' ? a.video.formatSlug : a.imagen.formatSlug;
}

/** Créditos de la tanda que saldría con lo que está puesto: la misma cuenta que el modo de origen. */
export function creditosDeRedes(a: AjustesDeRedes, adjuntos: ChatAttachment[] = []): number {
  // Animar: 1 crédito por segundo, como el modo.
  if (esAnimar(a)) return duracionDeAnimar(a.video.ajustes.durationSeconds);
  if (a.medio !== 'video') return creditosPromptLibre(a.imagen.cantidad, a.imagen.calidad);
  // Con un video de referencia también se cobran sus segundos, a la misma tarifa
  // (ADR-2026-10-20-el-video-de-referencia-cobra-entrada-y-salida).
  const ref = adjuntos.find((x) => x.kind === 'video');
  if (!ref) return creditosCreadorVideo(a.video.ajustes);
  const { durationSeconds, quality, resolution } = a.video.ajustes;
  return creditsForVideoGeneral({
    durationSec: durationSeconds,
    model: quality === 'basica' ? 'standard' : 'pro',
    resolution: safeResolution(resolution),
    segundosDeEntrada: segundosDeEntradaACobrar({
      duracionSalida: durationSeconds,
      calidad: quality,
      tramoDisponible: ref.kind === 'video' ? ref.duracionSeg : null,
    }),
  });
}

/** ¿La tanda sale en Automático? Sólo en Imagen y con ese modo elegido. */
export function esAutomatico(a: AjustesDeRedes): boolean {
  return a.medio === 'imagen' && a.modo === 'automatico';
}

/** ¿La tanda sale en Animar imagen? Sólo en Video y con ese modo elegido. */
export function esAnimar(a: AjustesDeRedes): boolean {
  return a.medio === 'video' && a.modo === 'animar';
}

/**
 * Qué falta para poder generar, o `undefined` si nada. En Imagen manda la regla de Prompt
 * libre (hace falta texto), salvo en Automático, donde lo que hace falta es un producto y el
 * texto es opcional; en Video, la del Creador (alcanza un tipo elegido o un adjunto).
 */
export function faltaEnRedes(args: {
  ajustes: AjustesDeRedes;
  prompt: string;
  listo: boolean;
  cantidadAdjuntos: number;
  /** Productos adjuntos: Automático necesita al menos uno. */
  cantidadProductos?: number;
  /** Imágenes adjuntas (Añadir referencia): Animar necesita exactamente una. */
  cantidadImagenes?: number;
}): string | undefined {
  if (esAutomatico(args.ajustes)) {
    if (!args.listo) return 'Cargando tu cuenta…';
    if (!args.cantidadProductos) return 'Agregá un producto: Atlas arma la escena con él';
    return undefined;
  }
  if (esAnimar(args.ajustes)) {
    if (!args.listo) return 'Cargando tu cuenta…';
    if (!args.cantidadImagenes) return 'Elegí la foto a animar en Añadir referencia';
    if (args.cantidadImagenes > 1) return 'Animar usa una sola foto: dejá una en Añadir referencia';
    if (!args.prompt.trim()) return 'Contá cómo se mueve la imagen';
    return undefined;
  }
  if (args.ajustes.medio === 'video') {
    return faltaCreadorVideo({
      prompt: args.prompt,
      listo: args.listo,
      tipoVideo: args.ajustes.video.tipoVideo,
      cantidadAdjuntos: args.cantidadAdjuntos,
    });
  }
  return faltaPromptLibre({ prompt: args.prompt, listo: args.listo });
}

/**
 * Los adjuntos repartidos como los muestra la caja: UN producto y UN modelo en sus
 * cuadrados, y el resto (imágenes subidas, poses, fondos, creaciones) en los chips. Si
 * hubiera más de un producto o modelo —ahora puede: el selector deja agregar varios—, el primero va
 * al cuadrado y los demás a los chips, así nunca se pierde uno de vista.
 */
export function repartirAdjuntos(adjuntos: ChatAttachment[]): {
  producto: ChatAttachment | null;
  modelo: ChatAttachment | null;
  otros: ChatAttachment[];
} {
  let producto: ChatAttachment | null = null;
  let modelo: ChatAttachment | null = null;
  const otros: ChatAttachment[] = [];
  for (const a of adjuntos) {
    if (a.kind === 'product' && !producto) producto = a;
    else if (a.kind === 'model' && !modelo) modelo = a;
    else otros.push(a);
  }
  return { producto, modelo, otros };
}

/**
 * Suma un adjunto a la lista, sin repetir el mismo id. TODO se acumula, también productos
 * y modelos: el selector deja marcar varios de una y agregarlos juntos
 * (ADR-2026-10-04-el-selector-de-fuentes-elige-varios). Antes un producto o un modelo
 * nuevo reemplazaba al anterior —eran un cuadrado cada uno—; ahora su tarjeta muestra el
 * abanico y "N productos", y se vacía con su X.
 */
export function conAdjunto(adjuntos: ChatAttachment[], nuevo: ChatAttachment): ChatAttachment[] {
  if (adjuntos.some((a) => a.id === nuevo.id)) return adjuntos;
  // La excepción es el VIDEO de referencia: el proveedor acepta uno solo, así que uno
  // nuevo reemplaza al anterior (ADR-2026-10-05-bloqueos-del-video-de-referencia).
  if (nuevo.kind === 'video') return [...adjuntos.filter((a) => a.kind !== 'video'), nuevo];
  return [...adjuntos, nuevo];
}

// ── El video de referencia y sus topes — ADR-2026-10-05-bloqueos-del-video-de-referencia ──

/**
 * Cuánto puede durar el video de REFERENCIA según la calidad con que se genera: el
 * proveedor rechaza una entrada que llegue al tope de salida de su modelo, así que es ese
 * tope menos uno (básica hasta 15 s → 14; profesional hasta 30 s → 29). Espejo de
 * `topeDeVideoDeReferencia` del backend y de `duracionConVideoDeReferencia.ts`.
 */
export const TOPE_DE_VIDEO_DE_REFERENCIA_S = { basica: 14, profesional: 29 } as const;

/** La duración conocida del video de referencia adjunto, o `null` si no hay o no se sabe. */
export function duracionDelVideoDeReferencia(adjuntos: ChatAttachment[]): number | null {
  const v = adjuntos.find((a) => a.kind === 'video');
  return v && v.kind === 'video' && v.duracionSeg > 0 ? v.duracionSeg : null;
}

const segundos = (n: number): string => `${Math.round(n)} s`;

/**
 * Qué hacer al SUMAR un video como referencia, según lo que dura:
 *  • entra en la calidad actual → `ok`;
 *  • no entra en Básica pero sí en Profesional → `subir` (se pasa la calidad, con aviso);
 *  • no entra ni en Profesional → `bloquear`, con el motivo.
 * Sin duración conocida no se puede decidir: entra, y el backend recorta el tramo.
 */
export function alSumarVideoDeReferencia(
  duracionSeg: number,
  quality: AjustesVideo['quality'],
): { accion: 'ok' } | { accion: 'subir'; aviso: string } | { accion: 'bloquear'; motivo: string } {
  if (!(duracionSeg > 0)) return { accion: 'ok' };
  if (duracionSeg > TOPE_DE_VIDEO_DE_REFERENCIA_S.profesional) {
    return {
      accion: 'bloquear',
      motivo: `Ese video dura ${segundos(duracionSeg)} y como referencia se aceptan hasta ${TOPE_DE_VIDEO_DE_REFERENCIA_S.profesional} s. Recortalo en el Estudio y sumá el recorte.`,
    };
  }
  if (quality === 'basica' && duracionSeg > TOPE_DE_VIDEO_DE_REFERENCIA_S.basica) {
    return {
      accion: 'subir',
      aviso: `El video dura ${segundos(duracionSeg)}: la calidad Básica acepta referencias de hasta ${TOPE_DE_VIDEO_DE_REFERENCIA_S.basica} s, así que pasé a Profesional.`,
    };
  }
  return { accion: 'ok' };
}

/**
 * Por qué NO se puede generar con el video de referencia que hay puesto, o `undefined`. Es
 * la red para lo que cambia después de sumarlo: bajar la calidad a Básica con una
 * referencia de más de 14 s. Sólo cuenta en Video.
 */
export function bloqueoPorVideoDeReferencia(a: AjustesDeRedes, adjuntos: ChatAttachment[]): string | undefined {
  if (a.medio !== 'video') return undefined;
  // Lo que Seedance rechaza y no tiene arreglo (proporción, menos de 2 s): no se manda a
  // esperar un error (ADR-2026-10-20-el-video-de-referencia-se-ajusta-a-lo-que-acepta-seedance).
  const video = adjuntos.find((x) => x.kind === 'video');
  const aviso = video && video.kind === 'video' ? avisoDeVideoDeReferencia(video) : null;
  if (aviso?.grave) return aviso.texto;
  const dur = duracionDelVideoDeReferencia(adjuntos);
  if (dur === null) return undefined;
  const calidad = a.video.ajustes.quality === 'basica' ? 'basica' : 'profesional';
  const tope = TOPE_DE_VIDEO_DE_REFERENCIA_S[calidad];
  if (dur <= tope) return undefined;
  return calidad === 'basica'
    ? `El video de referencia dura ${segundos(dur)}: la calidad Básica acepta hasta ${tope} s. Pasá a Profesional o quitá el video.`
    : `El video de referencia dura ${segundos(dur)} y se aceptan hasta ${tope} s. Quitalo o usá un recorte.`;
}

/** El título con el que nace la sesión: el prompt recortado, o el medio si no hay texto. */
export const TITULO_DE_SESION_MAX = 60;
export function tituloDeSesion(prompt: string, medio: Medio): string {
  const limpio = prompt.replace(/\s+/g, ' ').trim();
  if (!limpio) return medio === 'video' ? 'Video para redes' : 'Imagen para redes';
  return limpio.length > TITULO_DE_SESION_MAX
    ? `${limpio.slice(0, TITULO_DE_SESION_MAX - 1).trimEnd()}…`
    : limpio;
}

/**
 * El rótulo de la pastilla de formato: el nombre sin la proporción más la proporción
 * ("Posteo (4:5)" → "Posteo · 4:5"). Un slug desconocido muestra el slug.
 */
export function rotuloDeFormato(slug: string): string {
  // Un formato personalizado se nombra con su nombre y medida (ADR-2026-10-20-formatos-personalizados).
  if (esFormatoPersonalizado(slug)) return getFormatLabel(slug);
  const opt = FORMAT_OPTIONS_V2.find((f) => f.id === slug);
  if (!opt) return slug;
  const m = opt.name.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  return m ? `${m[1]} · ${m[2]}` : opt.name;
}

/** Placeholder de la caja según el medio (y, en video, si hay un tipo elegido). */
export function placeholderDeRedes(a: AjustesDeRedes): string {
  if (esAnimar(a)) return 'Cómo se mueve la imagen (por ejemplo, "la cámara se acerca despacio")…';
  if (a.medio === 'video') {
    return a.video.tipoVideo === GENERAL.slug
      ? 'Describí el video que querés crear…'
      : 'Indicaciones para el video (opcional)…';
  }
  if (esAutomatico(a)) return 'Opcional: sumá una idea (por ejemplo, "en la playa, con luz cálida")…';
  return 'Describí la imagen que querés crear…';
}

// ── "Mejorar prompt" y no escribir dos veces — ADR-2026-10-04-mejorar-prompt-en-redes ──

/**
 * Lo que había en la caja cuando el escritor devolvió su prompt: el texto devuelto y el
 * contexto con el que lo escribió. Al generar, si todo eso sigue igual (o el texto cambió
 * poco), el pedido viaja con `promptEscrito` y el Creador no lo vuelve a escribir.
 */
export interface HuellaDeMejora {
  /** Para qué medio se escribió: un prompt de video no vale para imagen ni al revés. */
  medio: Medio;
  prompt: string;
  /** Sólo video ('' y 0 en imagen). */
  tipoVideo: string;
  duracion: number;
  formatSlug: string;
  /** Ids de los adjuntos, ordenados: con otra referencia el escritor diría otra cosa. */
  adjuntos: string;
  /**
   * Sólo imagen: lo que el escritor DECLARÓ junto con el prompt (con qué motor conviene y
   * qué es cada foto subida). Se guarda para mandarlo tal cual al generar y no volver a
   * decidirlo (ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente).
   */
  declarado?: DeclaradoDeImagen;
}

/** Lo que el escritor de imagen declara de una generación, como lo declara el agente. */
export interface DeclaradoDeImagen {
  tarea: 'crear' | 'editar' | 'componer' | 'explorar' | 'grafica' | 'cine';
  /** `conservar` | `fusionar` (ADR-2026-10-07-conservar-o-fusionar). */
  edicion?: 'conservar' | 'fusionar';
  pielExpuesta: boolean;
  fondoBlanco: boolean;
  luzFondoBlanco: 'difusa' | 'dura';
  /** Qué es cada foto subida ("Pose: de perfil"), por el id del adjunto. */
  etiquetas: Array<{ adjuntoId: string; etiqueta: string }>;
}

export function huellaDeMejora(
  promptDevuelto: string,
  a: AjustesDeRedes,
  adjuntos: ChatAttachment[],
  declarado?: DeclaradoDeImagen,
): HuellaDeMejora {
  const esVideo = a.medio === 'video';
  return {
    medio: a.medio,
    prompt: promptDevuelto,
    tipoVideo: esVideo ? a.video.tipoVideo : '',
    duracion: esVideo ? a.video.ajustes.durationSeconds : 0,
    formatSlug: esVideo ? a.video.formatSlug : a.imagen.formatSlug,
    adjuntos: adjuntos.map((x) => x.id).sort().join(','),
    ...(declarado && !esVideo ? { declarado } : {}),
  };
}

/**
 * Cuánto tarda "Mejorar prompt", para la barra de carga del botón
 * (ADR-2026-10-04-el-boton-de-mejorar-es-una-barra-de-carga). Es la media de las mejoras
 * reales medidas el 2026-10-04 en `executions` (creada → terminada): imagen 16,5 / 17,3 /
 * 16,2 / 12,3 s (media 15,6) y video 13,6 s (una sola), más el viaje de red. Para volver a
 * medir: `select type, avg(extract(epoch from finished_at - created_at)) from executions
 * where type in ('escribir_prompt_imagen','escribir_prompt_video') and status='succeeded'
 * group by 1`.
 */
export const DURACION_MEDIA_DE_MEJORA_MS: Record<Medio, number> = { imagen: 16_000, video: 14_000 };

/** Hasta qué proporción de palabras tocadas un retoque sigue siendo "leve". */
export const CAMBIO_LEVE_MAX = 0.15;

const palabrasDe = (texto: string): string[] => texto.toLowerCase().split(/\s+/).filter(Boolean);

/** Largo de la subsecuencia común más larga, por palabras. */
function largoComun(a: string[], b: string[]): number {
  let fila = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const nueva = new Array<number>(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j++) {
      nueva[j] = a[i - 1] === b[j - 1] ? fila[j - 1] + 1 : Math.max(fila[j], nueva[j - 1]);
    }
    fila = nueva;
  }
  return fila[b.length];
}

/**
 * Qué proporción del texto cambió entre dos versiones, por palabras: las que se sacaron
 * más las que se sumaron, sobre el largo de la más larga. 0 = iguales; 1 = nada en común.
 */
export function proporcionDeCambio(antes: string, despues: string): number {
  const a = palabrasDe(antes);
  const b = palabrasDe(despues);
  const mayor = Math.max(a.length, b.length);
  if (mayor === 0) return 0;
  const comun = largoComun(a, b);
  return (a.length - comun + (b.length - comun)) / mayor;
}

export const esCambioLeve = (antes: string, despues: string): boolean =>
  proporcionDeCambio(antes, despues) <= CAMBIO_LEVE_MAX;

/**
 * Si el prompt que se va a generar ya es el del escritor: hubo una mejora para ESTE medio,
 * el contexto (formato y adjuntos; en video también tipo y duración) es el mismo y el texto
 * quedó igual o con un retoque leve. Con cualquier otra cosa distinta, el escritor vuelve a
 * correr. En imagen, la cantidad y la calidad no cuentan: no cambian lo que se escribe.
 */
export function promptYaEscrito(
  huella: HuellaDeMejora | null,
  prompt: string,
  a: AjustesDeRedes,
  adjuntos: ChatAttachment[],
): boolean {
  if (!huella || huella.medio !== a.medio || !prompt.trim()) return false;
  const ahora = huellaDeMejora(prompt, a, adjuntos);
  return (
    ahora.tipoVideo === huella.tipoVideo &&
    ahora.duracion === huella.duracion &&
    ahora.formatSlug === huella.formatSlug &&
    ahora.adjuntos === huella.adjuntos &&
    esCambioLeve(huella.prompt, prompt)
  );
}
